import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { DeploymentStep } from './DeploymentStep';
import { ExternalAppsStep } from './ExternalAppsStep';
import { IdentityStep, IdentityStepHandle } from './IdentityStep';
import { KnowledgeBaseStep, KnowledgeBaseStepHandle } from './KnowledgeBaseStep';
import { OnboardingChecklist } from './OnboardingChecklist';
import { OnboardingProgressDots } from './OnboardingProgressDots';
import { PricingStepContainer } from './PricingStepContainer';
import { GettingStartedChecklistItem, GsStepId } from './useGettingStartedOnboarding';
import { WebsiteModalTypeEnum } from '@/Domain/enum/WebsiteModalType.enum';
import { useImportUrl } from '@/Domain/hooks/KnowledgeBase/useImportUrl.hook';
import { useUploadArticles } from '@/Domain/hooks/KnowledgeBase/useUploadArticles.hook';
import { MODAL_NAMES } from '@/Domain/store/modal.store';

/**
 * GettingStarted-scoped onboarding progress card.
 *
 * Pure presentational component — state is owned by the parent page so the same
 * `useGettingStartedOnboarding` result can also drive widget/onboarding view
 * switching on completion.
 *
 * Pricing (`gs_step_5`) renders in its own wider container, cross-fading with the checklist.
 * Progress dots sit outside the AnimatePresence so they don't flash during the swap.
 * Renders nothing once onboarding is complete (page swaps in the widget view).
 */

interface OnboardingProgressProps {
  steps: { id: GsStepId; completed: boolean; skipped: boolean }[];
  completed_count: number;
  total_count: number;
  skipStep: (stepId: GsStepId, skip: boolean) => void;
  completeStep: (stepId: GsStepId, complete: boolean) => void;
  isSkipPending?: boolean;
  /**
   * Called when the user hits Back on the very first step. Takes them back
   * to the URL-first entry screen (by flipping `mode` → `'automatic'`).
   * Every later step's Back uses the default "un-complete the previous
   * step" behavior instead.
   */
  onBackToUrlStep: () => void;
}

const PRICING_STEP_ID: GsStepId = 'gs_step_5';
const PRE_PRICING_STEP_ID: GsStepId = 'gs_step_4';

// Shared motion props for the container cross-fade — match the inner
// step-content transition in OnboardingChecklist so the two transitions
// (intra-checklist and checklist↔pricing) feel identical.
const CONTAINER_FADE = {
  initial: { opacity: 0, y: 6 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -6 },
  transition: { duration: 0.2 },
};

export const OnboardingProgress = ({
  steps,
  completed_count,
  total_count,
  skipStep,
  completeStep,
  isSkipPending = false,
  onBackToUrlStep,
}: OnboardingProgressProps) => {
  const { t } = useTranslation('getting_started');

  // Knowledge Base step — the file the user has selected but not yet uploaded.
  // Kept as session state (not persisted) so navigating away resets the picker.
  const [pendingKbFile, setPendingKbFile] = useState<File | null>(null);
  // Scrape URL input state. `null` = scrape option not opened; `''` or any
  // string = user has entered scrape mode. Value matches what the KB modal's
  // `UrlInput` emits — already includes the `https://` protocol for non-empty
  // inputs via `AppHelper.addProtocol`, so we don't prepend it again here.
  const [pendingKbUrl, setPendingKbUrl] = useState<string | null>(null);
  const { handleUploadFiles } = useUploadArticles();
  const { importUrl } = useImportUrl();

  // Identity step owns its own RHF instance. The parent only needs to ask it
  // "are you valid?" before letting the user advance — expose that via a ref
  // so we can trigger validation and surface per-field errors without lifting
  // the form state out of the step component.
  const identityStepRef = useRef<IdentityStepHandle>(null);
  const handleIdentityContinue = useCallback(async () => {
    const ok = (await identityStepRef.current?.validate()) ?? false;
    if (ok) completeStep('gs_step_1', true);
  }, [completeStep]);

  // Same pattern as the Identity step: the KB step owns its URL error state
  // and exposes `validate()` so Continue can gate advancement on a valid URL
  // (the step otherwise silently marked itself complete on malformed input).
  const kbStepRef = useRef<KnowledgeBaseStepHandle>(null);

  // When the user hits Continue on the Knowledge Base step, kick off the same
  // mutation the Knowledge tab uses — upload the selected file, or scrape the
  // entered URL — then mark the step complete. Fire-and-forget: the backend
  // indexing job has its own state machine, we don't need to await it to
  // advance onboarding. Both inputs are mutually exclusive in the UI.
  //
  // Error handling: the KB tab's `UploadUrlModal` surfaces backend errors
  // inline via the modal's own `setError` wiring. Here there's no modal to
  // write into (user has already advanced), so we surface backend errors as
  // a global sonner toast. Without this the onboarding silently swallowed
  // 4xx/5xx from `/import_external/website` (unsupported sites, DNS
  // failures, etc.) and the user couldn't tell scraping never started.
  // validate() now awaits a DoH lookup: snapshot inputs + a mount flag so a stale continuation (edited URL / Back-to-URL unmount) can't import.
  const pendingKbFileRef = useRef(pendingKbFile);
  pendingKbFileRef.current = pendingKbFile;
  const pendingKbUrlRef = useRef(pendingKbUrl);
  pendingKbUrlRef.current = pendingKbUrl;
  const isMountedRef = useRef(true);
  useEffect(() => {
    // Re-arm on setup: StrictMode replays setup→cleanup→setup, which would otherwise leave the flag stuck false and drop every Continue in dev.
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleKnowledgeBaseContinue = useCallback(async () => {
    const file = pendingKbFile;
    const urlValue = pendingKbUrl;
    if (!((await kbStepRef.current?.validate()) ?? true)) return;
    if (
      !isMountedRef.current ||
      file !== pendingKbFileRef.current ||
      urlValue !== pendingKbUrlRef.current
    ) {
      return;
    }
    if (file) {
      void handleUploadFiles([file]);
    } else if (urlValue && urlValue.trim() !== '') {
      const url = urlValue.trim();
      // Seed the KB with the entered page right away (parity with automatic
      // onboarding, where the backend ingests the seed page in Step 3), then map
      // the whole site so the dock can offer the page-level review post-wizard.
      void importUrl({
        url,
        type: WebsiteModalTypeEnum.SINGLE,
        modalId: MODAL_NAMES.UPLOAD_URL_MODAL_ID,
        setError: (message: string) => toast.error(message),
        silent: true,
      }).then(() =>
        importUrl({
          url,
          type: WebsiteModalTypeEnum.ALL_PAGES,
          modalId: MODAL_NAMES.UPLOAD_URL_MODAL_ID,
          setError: (message: string) => toast.error(message),
          silent: true,
        }),
      );
    }
    completeStep('gs_step_2', true);
    // Deferred past AnimatePresence exit so KB step doesn't morph mid-fade.
    setTimeout(() => {
      setPendingKbFile(null);
      setPendingKbUrl(null);
    }, 300);
  }, [pendingKbFile, pendingKbUrl, handleUploadFiles, importUrl, completeStep]);

  // Back un-completes deployment (drops back to step 4); Skip skips pricing.
  const handlePricingBack = useCallback(
    () => completeStep(PRE_PRICING_STEP_ID, false),
    [completeStep],
  );
  const handlePricingSkipForNow = useCallback(() => skipStep(PRICING_STEP_ID, true), [skipStep]);

  // Pricing is filtered out and rendered separately by PricingStepContainer below.
  const checklist: GettingStartedChecklistItem[] = useMemo(
    () =>
      steps
        .filter((step) => step.id !== PRICING_STEP_ID)
        .map((step, index) => ({
          ...step,
          title: t(`steps.${step.id}.title`),
          description: t(`steps.${step.id}.description`),
          // Steps with inline UI (forms, option tiles) replace the static description.
          expandedContent:
            step.id === 'gs_step_1' ? (
              <IdentityStep ref={identityStepRef} />
            ) : step.id === 'gs_step_2' ? (
              <KnowledgeBaseStep
                ref={kbStepRef}
                file={pendingKbFile}
                onFileChange={setPendingKbFile}
                url={pendingKbUrl}
                onUrlChange={setPendingKbUrl}
              />
            ) : step.id === 'gs_step_3' ? (
              <ExternalAppsStep />
            ) : step.id === 'gs_step_4' ? (
              <DeploymentStep />
            ) : undefined,
          // Every step in the checklist gets a "Continue" CTA — Pricing is the
          // real last step but lives outside the checklist, so none of these
          // 4 should ever read "Finish".
          primaryActionLabel: t('continue'),
          // Every step gets a Back link. Steps 2+ un-complete the previous step
          // (default handler); step 1 bails out to the URL-first entry screen
          // via `onBackAction` so the user can re-enter a URL or re-pick a path.
          showBackAction: true,
          onBackAction: index === 0 ? onBackToUrlStep : undefined,
          // Identity step requires both fields before advancing. KB step owns
          // its own Continue behavior (upload the file, then complete).
          onPrimaryAction:
            step.id === 'gs_step_1'
              ? handleIdentityContinue
              : step.id === 'gs_step_2'
                ? handleKnowledgeBaseContinue
                : undefined,
        })),
    [
      steps,
      t,
      pendingKbFile,
      pendingKbUrl,
      handleKnowledgeBaseContinue,
      handleIdentityContinue,
      onBackToUrlStep,
    ],
  );

  if (completed_count === total_count) {
    return null;
  }

  // The "current" step drives both the AnimatePresence branch and the dot
  // highlight. Once everything's done OnboardingProgress has already returned
  // null above, so `currentIndex` is always >=0 here.
  const currentIndex = steps.findIndex((s) => !s.completed && !s.skipped);
  const isPricingActive = steps[currentIndex]?.id === PRICING_STEP_ID;

  return (
    <motion.div
      key="progress"
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.3 }}
      className="flex flex-col gap-4 w-full items-center"
      data-testid="gs-onboarding-progress"
    >
      <AnimatePresence mode="wait">
        {isPricingActive ? (
          <motion.div key="pricing-container" {...CONTAINER_FADE} className="w-full max-w-6xl">
            <PricingStepContainer
              onBack={handlePricingBack}
              onSkipForNow={handlePricingSkipForNow}
              isSkipPending={isSkipPending}
            />
          </motion.div>
        ) : (
          <motion.div key="checklist-container" {...CONTAINER_FADE} className="w-full max-w-lg">
            <OnboardingChecklist
              checklist={checklist}
              onSkip={skipStep}
              onComplete={completeStep}
              isSkipPending={isSkipPending}
            />
          </motion.div>
        )}
      </AnimatePresence>
      <OnboardingProgressDots
        totalSteps={steps.length}
        currentIndex={currentIndex}
        testIdPrefix="gs-onboarding-progress-dot"
      />
    </motion.div>
  );
};
